import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api';
import { toast } from 'react-toastify';

const MockExamSubscriptionList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parentStudents, setParentStudents] = useState([]);
  const [assigningPurchaseId, setAssigningPurchaseId] = useState(null);
  const [downloadingPurchaseId, setDownloadingPurchaseId] = useState(null);
  const autoAssignedRef = useRef(new Set());

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const response = await api.get('parent/mock-exam/purchases');
      if (response.data?.success) {
        const raw = response.data.data;
        const list = Array.isArray(raw) ? raw : [];
        setRows(list);
      } else {
        setRows([]);
        toast.error(response.data?.message || 'Failed to load mock exam purchases');
      }
    } catch (error) {
      setRows([]);
      if (error.response?.status === 401) {
        toast.error('Please log in as a parent to view mock exam purchases.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load mock exam purchases');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchases();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await api.get('parent/students/names');
        if (response.data.success && Array.isArray(response.data.data)) {
          setParentStudents(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching parent students for mock exam assign:', err);
      }
    };
    fetchStudents();
  }, []);

  const handleAssignStudent = useCallback(async (purchaseId, studentId) => {
    setAssigningPurchaseId(purchaseId);
    try {
      const response = await api.put(`parent/mock-exam/purchases/${purchaseId}/assign-student`, {
        student_id: studentId ? parseInt(studentId, 10) : null,
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Assignment updated.');
        const data = response.data.data || {};
        setRows((prev) =>
          prev.map((p) =>
            p.purchase_id === purchaseId
              ? {
                  ...p,
                  student_id: data.student_id ?? null,
                  student_name: data.student_name ?? p.student_name,
                  student_email: data.student_email ?? p.student_email,
                }
              : p
          )
        );
      } else {
        toast.error(response.data.message || 'Failed to update assignment.');
      }
    } catch (error) {
      console.error('Error assigning student to mock exam:', error);
      toast.error(error.response?.data?.message || 'Failed to update assignment.');
    } finally {
      setAssigningPurchaseId(null);
    }
  }, []);

  // When parent has only one student, auto-assign purchases that have no student yet
  useEffect(() => {
    if (parentStudents.length !== 1 || rows.length === 0) return;
    const studentId = parentStudents[0].id;
    const unassigned = rows.filter(
      (p) => !p.student_id && !autoAssignedRef.current.has(p.purchase_id)
    );
    unassigned.forEach((purchase) => {
      autoAssignedRef.current.add(purchase.purchase_id);
      handleAssignStudent(purchase.purchase_id, studentId);
    });
  }, [parentStudents, rows, handleAssignStudent]);

  const handleDownloadReceipt = async (purchaseId) => {
    setDownloadingPurchaseId(purchaseId);
    try {
      const response = await api.get('parent/mock-exam/purchase/receipt', {
        params: { purchase_id: purchaseId },
      });
      if (response.data.success && response.data.data.receipt_url) {
        window.open(response.data.data.receipt_url, '_blank');
      } else {
        toast.error(response.data.message || 'Receipt URL not available.');
      }
    } catch (error) {
      console.error('Error fetching mock exam receipt:', error);
      toast.error(error.response?.data?.message || 'Failed to load receipt.');
    } finally {
      setDownloadingPurchaseId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress size={40} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 8,
          textAlign: 'center',
          borderRadius: 4,
          border: '2px dashed rgba(102, 126, 234, 0.1)',
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #f0f4ff 0%, #eef2ff 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.1)',
          }}
        >
          <QuizIcon sx={{ fontSize: 40, color: '#667eea' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="#2d3748">
          No mock exam purchases yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.6 }}>
          When you buy a mock exam from the portal, it will appear here with amount, payment status, and assigned student.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        overflowX: 'auto',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>
            <TableCell sx={{ fontWeight: 700 }}>Mock exam</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Grade / year</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Subject(s)</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Assign to student</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Purchased</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.purchase_id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.exam_name || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.duration_minutes || 0} min | {row.total_marks || 0} marks
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 160 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {row.school_name || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.grade_name || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 220 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {row.subject_names || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ position: 'relative', display: 'inline-block', minWidth: 168 }}>
                  {parentStudents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {row.student_name || 'Not assigned'}
                    </Typography>
                  ) : parentStudents.length === 1 ? (
                    <>
                      <Typography variant="body2" fontWeight={500}>
                        {parentStudents[0].full_name}
                        {assigningPurchaseId === row.purchase_id && (
                          <CircularProgress size={14} sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.student_email || parentStudents[0].email || '—'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <FormControl size="small" sx={{ minWidth: 160 }} disabled={assigningPurchaseId === row.purchase_id}>
                        <Select
                          value={row.student_id ?? ''}
                          onChange={(e) => handleAssignStudent(row.purchase_id, e.target.value)}
                          displayEmpty
                          sx={{
                            fontSize: '0.875rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                          }}
                        >
                          <MenuItem value="">
                            <em>Unassigned</em>
                          </MenuItem>
                          {parentStudents.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.full_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {assigningPurchaseId === row.purchase_id && (
                        <CircularProgress
                          size={18}
                          sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)' }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                {Number(row.price || 0).toFixed(2)} {row.currency || 'GBP'}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.payment_status || 'PENDING'}
                  color={(row.payment_status || '').toUpperCase() === 'PAID' ? 'success' : 'warning'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{row.purchased_at || '—'}</TableCell>
              <TableCell align="center">
                {(row.payment_status || '').toUpperCase() === 'PAID' && (
                  <Box
                    component="button"
                    onClick={() => handleDownloadReceipt(row.purchase_id)}
                    disabled={downloadingPurchaseId === row.purchase_id}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: '1px solid #16a34a',
                      bgcolor: 'rgba(22, 163, 74, 0.05)',
                      color: '#16a34a',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(22, 163, 74, 0.1)',
                      },
                      '&:disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {downloadingPurchaseId === row.purchase_id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    )}
                    Receipt
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MockExamSubscriptionList;
